import {
  AmbientLight,
  CanvasRenderer,
  Color,
  DirectionalLight,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  PerspectiveCamera,
  PlaneGeometry,
  Scene,
  WebGLRenderer
} from 'three'
import {DEFAULT_CONTAINER_HEIGHT} from 'js/render/constants'
import {noop} from 'underscore'
import Controls from './controls'
import Doctor from 'js/render/doctor'
import GenericLoader from './genericLoader'
import RenderEventDispatcher from './eventDispatcher'
import Renderable from './renderable'
import ThreeDDiff from './diff'
import STLLoader from './loaders/stlLoader'

const getDefaultOptions = () => ({
  url: '',
  name: '',
  center: true,
  autoscale: false,
  side: {}
})

class ThreeDViewer extends RenderEventDispatcher {
  //# External API
  constructor(container, options, ready) {
    super()
    let loadHandler
    this.draw = this.draw.bind(this)
    this.load = this.load.bind(this)
    this.destructor = this.destructor.bind(this)
    this.width = this.width.bind(this)
    this.height = this.height.bind(this)
    this.resize = this.resize.bind(this)
    this.watchdog = this.watchdog.bind(this)
    this.init = this.init.bind(this)
    this.objectLoadHandler = this.objectLoadHandler.bind(this)
    this.updateLight = this.updateLight.bind(this)
    this.updateGrid = this.updateGrid.bind(this)
    this.selectRenderer = this.selectRenderer.bind(this)

    this.$ = this.$.bind(this)
    this.container = container
    this.$container = $(this.container)

    if (options instanceof Function) {
      ready = options
      options = {}
    }

    // handle the case of false or null being explicitly passed as an argument, I suppose
    if (!options) {
      options = {}
    }
    if (!ready) {
      ready = noop
    }

    if (!this.$container.length) {
      return
    }
    if (this.$container.data('bound')) {
      return
    }

    // Used for notifing the user of lack of WebGL
    this.doctor = new Doctor(this.container)

    this.status = new window.Render.Status('solid')

    // Handle object loading
    this.objects = {}
    this.once('loaded', () => this.draw(true))
    this.addEventListener('loaded', this.objectLoadHandler)

    this.addEventListener(
      'load',
      (loadHandler = event => {
        const {data} = event
        if (['error', 'error:fatal'].includes(data)) {
          this.status.set(data)
        } else if (data === 'finish') {
          // We always resize the render container to a static height,
          // so it is safe to post this message following a successful load of
          // the stl file
          this.status.set('resize', {height: DEFAULT_CONTAINER_HEIGHT})
          this.removeEventListener('load', loadHandler)
        }
      })
    )

    this.once('error', e => {
      this.status.set('error')
      throw new Error(e)
    })

    // Fire resize once after STL is loaded for final dimensions
    this.once('loaded', this.resize)

    // Boot the viewer and
    // invoke the ready callback
    $(window).on('resize', this.resize)
    this.init(options, (...args) => {
      this.$container.data('bound', true)
      ready.call(this, ...args)
    })
  }

  draw(start) {
    const tickDraw = tick => {
      this.dispatchEvent({
        type: 'tick',
        data: tick
      })
      this.updateLight(this.camera.position)
      this.renderer.render(this.scene, this.camera)
    }
    const drawLoop = tick => {
      if (!tick) {
        tick = 0
      }
      tickDraw(tick)
      if (this.running) {
        requestAnimationFrame(drawLoop)
      }
    }

    if (start === false) {
      this.running = false
    } else if (start === true) {
      if (this.running) {
        return
      }
      this.running = true
      drawLoop()
    } else {
      tickDraw()
    }
  }

  addLoaderEvents(loader, opts = {}) {
    loader.addEventListener('load', event => {
      this.dispatchEvent({
        type: 'load',
        data: 'finish'
      })

      this.dispatchEvent({
        type: 'loaded',
        object: new Renderable(event.content, opts)
      })
    })

    loader.addEventListener('error', event => {
      this.dispatchEvent({
        type: 'load',
        data: 'error'
      })
      window.debug('Load Error:', event)
    })

    loader.addEventListener('error:fatal', event => {
      this.dispatchEvent({
        type: 'load',
        data: 'error:fatal'
      })
      window.debug('Load Error:', event)
    })
  }

  loadMarkdown(data, options = {}) {
    const objOpts = {
      ...getDefaultOptions(),
      ...options
    }

    this.dispatchEvent({
      type: 'load',
      data: 'start'
    })

    const loader = new STLLoader(this.status)
    objOpts.side = loader.side()

    this.addLoaderEvents(loader, objOpts)

    loader.afterLoadSuccess(data)
  }

  load(url, options = {}) {
    const objOpts = {
      ...getDefaultOptions(),
      ...options
    }

    this.dispatchEvent({
      type: 'load',
      data: 'start'
    })

    const loader = GenericLoader.loaderForUrl(this.status, url)
    objOpts.side = loader.side()

    this.addLoaderEvents(loader, objOpts)

    loader.load(url)
  }

  //# Internal API
  destructor() {
    // Called when the viewer element is
    // no longer attached to the DOM.
    this.draw(false)
  }

  width() {
    return (
      (window.parent === window ? $(window).innerWidth() : undefined) || this.$().width() || $(window).width() || 500
    )
  }

  height() {
    return (
      (window.parent === window ? $(window).innerHeight() : undefined) ||
      (this.$().height() || $(window).height() || 500) - $('.js-render-bar').outerHeight()
    )
  }

  resize() {
    const [width, height] = [this.width(), this.height()]
    if (!width || !height) {
      return
    } // Avoid a resize to anything with a zero
    this.camera.aspect = width / height
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(width, height)
    this.dispatchEvent({
      type: 'resize',
      data: {width, height}
    })

    window.debug(`Viewport now ${width}x${height}`)
  }

  watchdog() {
    const visible = $(this.container).length > 0
    if (!visible) {
      this.destructor()
    } else {
      $(document).one('pageUpdate', this.watchdog)
    }
  }

  init(options, ready) {
    if (!options) {
      options = {}
    }
    if (!ready) {
      ready = noop
    }
    if (this.$container.data('initialized')) {
      return
    }

    if (options instanceof Function) {
      ;[options, ready] = [{}, options]
    }

    const fail = msg => {
      if (!msg) {
        msg = 'unknown'
      }
      ready(msg || 'unknown', this)
      return this.dispatchEvent({
        type: 'error',
        data: msg
      })
    }

    this.once('loaded', () => {
      if (!this.controls) {
        this.controls = new Controls(this)
      }
      this.controls
    })
    this.once('init', () => {
      this.$container.data('initialized', true)
    })
    this.once('init', this.watchdog)
    this.once('init', this.doctor.updateHealth)
    this.once('init', () => this.resize())
    this.once('init', () => ready(false, this))

    if (!(this.renderer = this.selectRenderer())) {
      return fail() && undefined
    }

    this.renderer.setSize(this.width(), this.height())
    this.$().append(this.renderer.domElement)

    this.scene = new Scene()
    this.scene.background = new Color(0xffffff)
    this.scene.up.set(0, 0, 1)

    this.cameraFOV = 50
    this.camera = new PerspectiveCamera(this.cameraFOV, this.width() / this.height(), 0.01, 10000)
    this.camera.up.set(0, 0, 1)

    this.scene.add(this.camera)

    this.updateGrid()

    this.dispatchEvent({
      type: 'init',
      data: 'finished'
    })
  }

  objectLoadHandler(data) {
    let object
    if (!data) {
      return
    }
    if (!(object = data.object)) {
      return
    }
    if (!object.name) {
      return
    }
    if (this.objects[object.name]) {
      return
    }
    this.objects[object.name] = object

    if (object.info.scale !== 1.0) {
      const direction = object.info.scale > 1.0 ? 'up' : 'down'
      this.doctor.addProblem(`Object has been scaled ${direction} to fit the view`)
    }

    // Make sure that the object fits within our frustum
    const scaledRadius = object.geometry.boundingSphere.radius * 4
    if (!(this.camera.far > scaledRadius)) {
      this.camera.far = scaledRadius
    }
    this.camera.updateProjectionMatrix()

    this.updateGrid(2 * object.geometry.boundingSphere.radius)

    // If we're diffing files
    if (/^file\d+$/.test(object.name)) {
      if (!this.objects['diff'] && this.objects['file1'] && this.objects['file2']) {
        // Lets attach both geometries to a new diff object and only add that to the scene
        const file1 = this.objects['file1']
        const file2 = this.objects['file2']
        new ThreeDDiff(this, file1, file2, self => {
          this.objects['diff'] = self
          this.scene.add(self)
          this.dispatchEvent({type: 'added', data: self})
          this.dispatchEvent({type: 'diff:ready', data: self})
          this.status.set('ready')
          window.debug('Loaded diff:', self)
        })
      }
    } else {
      this.scene.add(object)
      this.dispatchEvent({type: 'added', data: object})
      this.status.set('ready')
    }
    window.debug('Loaded:', object)
  }

  //# Utils
  updateLight(cameraPosition) {
    const {x} = cameraPosition
    const {y} = cameraPosition
    const {z} = cameraPosition

    if (this.light) {
      this.light.position.set(x, y, z)
    } else {
      this.scene.add(new AmbientLight(0x404040))

      this.light = new DirectionalLight(0xffffff)
      this.light.position.set(x, y, z)
      this.scene.add(this.light)
    }
  }

  updateGrid(size) {
    let material
    let plane
    if (!size) {
      size = 1
    }
    const gridDistance = 10 // pixels
    const planeSize = 1.1 * size
    let numberOfGrids = Math.ceil(planeSize / gridDistance) + 10 // We want some buffer
    const gridSize = gridDistance * numberOfGrids
    const moveDown = new Matrix4().makeTranslation(0, 0, -1e-3)
    const gridLimit = 100

    if (numberOfGrids > gridLimit) {
      // We got a huge grid, lets limit it & let the user know they have a huge model
      numberOfGrids = gridLimit
      this.doctor.addProblem('The grid is being scaled because your model is too large')
    }

    if (this.grid) {
      this.grid.geometry.computeBoundingSphere()
      const currentSize = this.grid.geometry.boundingSphere.radius
      if (gridSize > currentSize) {
        plane = new PlaneGeometry(gridSize, gridSize, numberOfGrids, numberOfGrids)
        plane.applyMatrix4(moveDown)
        ;({material} = this.grid)
        this.scene.remove(this.grid)
        this.grid.geometry.dispose()

        this.grid = new Mesh(plane, material)
        this.scene.add(this.grid)
      }
    } else {
      plane = new PlaneGeometry(gridSize, gridSize, numberOfGrids, numberOfGrids)
      plane.applyMatrix4(moveDown)
      material = new MeshBasicMaterial({color: 0x333333, wireframe: true})

      this.grid = new Mesh(plane, material)
      this.scene.add(this.grid)
    }
  }

  selectRenderer(gl = true) {
    if (gl && window.WebGLRenderingContext) {
      try {
        return new WebGLRenderer({antialias: true, sortObjects: false})
      } catch (e) {
        window.debug('Failed to create WebGLRenderer:', e)
      }
    }

    this.doctor.addProblem('WebGL powered hardware support not available')
    return new CanvasRenderer()
  }

  $() {
    // Helper to reach $container
    if (!this.$container) {
      this.$container = $(this.container)
    }
    return this.$container
  }
}

export default ThreeDViewer
